import moment from "moment";
import React, { useEffect, useState } from "react";
import { useFetch } from "../Hooks/useFetch";
import { FetchBoundary } from "../common/FetchBoundary";
import { withTheme } from "../common/ThemeProviderWrapper";
import { Tab, Tabs } from "@mui/material";
import { AtRiskSuspendedPharmacyPie } from "./AtRiskSuspendedPharmacyPie";
import { capitalize } from "../utils/StringFormatters";

const pharmacySort = (a, b) => {
  // localeCompare treats dashes and uppercase differently, just pass in a clean slate
  const cleanName = (str) => {
    if (!str) return "";
    return str
      .replace(/[^a-zA-Z0-9]/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();
  };

  return (
    a.id_340b.localeCompare(b.id_340b) ||
    a.manufacturer?.localeCompare(b.manufacturer) ||
    a.network.localeCompare(b.network) ||
    cleanName(a.name).localeCompare(cleanName(b.name))
  );
};

const AtRiskSuspendedPharmacyCard = ({ organizationId }) => {
  const pharmaciesFetcher = useFetch(`/api/organization/${organizationId}/dashboard/pharmacies`);
  const [tab, setTab] = useState("at_risk");

  const pharmacies = pharmaciesFetcher.data?.pharmacies ?? [];
  const coveredEntityCount = pharmaciesFetcher.data?.covered_entity_count ?? 0;
  const manufacturerCount = pharmaciesFetcher.data?.manufacturer_count ?? 0;
  const eligiblePharmacyCount = pharmaciesFetcher.data?.eligible_pharmacy_count;

  const atRiskPharmacyCount = pharmacies.filter((p) => p.display_status === "at_risk").length;
  const suspendedPharmacyCount = pharmacies.filter((p) => p.display_status === "suspended").length;

  const activePharmacies = pharmacies.filter((p) => p.display_status === tab);

  useEffect(() => {
    if (pharmaciesFetcher.hasFetched && tab === "at_risk" && atRiskPharmacyCount == 0) {
      setTab("suspended");
    }
  }, [tab, atRiskPharmacyCount, pharmaciesFetcher.hasFetched]);

  return (
    <div className="div5">
      <div className="tile w--full h--full">
        <div className="tile__header tile__header--spread pb-4">
          <div className="tile__header__details">
            <div className="tile__header__details__title">
              Total At Risk and Suspended Pharmacies
            </div>
            <div className="tile__header__details__subtitle">
              Updated - {moment().format("MM-DD-YYYY")}
            </div>
          </div>
          <a href={`/organization/${organizationId}/reports/at_risk`}>
            <div className="button button--white">View Report</div>
          </a>
        </div>
        <FetchBoundary
          className="flex flex-column"
          style={{ height: 625, overflow: "hidden" }}
          {...pharmaciesFetcher}
        >
          <div className="flex align-items-center mb-2">
            <div className="flex align-items-center gap--8 pr-3">
              <span className="t--xl t--500">{atRiskPharmacyCount}</span>
              <span className="t--sm t--500 t--secondary">Total At Risk Pharmacies</span>
            </div>
            <div className="flex align-items-center gap--8">
              <span className="t--xl t--500">{suspendedPharmacyCount}</span>
              <span className="t--sm t--500 t--secondary">Total Suspended Pharmacies</span>
            </div>
          </div>
          <div className="t--sm t--secondary mb-4">
            You have <span className="t--500">{atRiskPharmacyCount}</span> At Risk and{" "}
            <span className="t--500">{suspendedPharmacyCount}</span> Suspended pharmacies across{" "}
            <span className="t--500">{coveredEntityCount}</span> entities and{" "}
            <span className="t--500">{manufacturerCount}</span> manufacturers. Review the pharmacies
            and submit claims to maintain access to pricing.
          </div>

          <div className="flex-shrink-0" style={{ height: "250px" }}>
            <AtRiskSuspendedPharmacyPie
              suspendedPharmacyCount={suspendedPharmacyCount}
              atRiskPharmacyCount={atRiskPharmacyCount}
              eligiblePharmacyCount={eligiblePharmacyCount}
            />
          </div>

          <div className="w--full my-4 border--bottom tab-border-bottom">
            <Tabs value={tab} onChange={(e, newValue) => setTab(newValue)}>
              <Tab
                disabled={atRiskPharmacyCount === 0}
                label={<div className="flex align-items-center">At Risk</div>}
                value="at_risk"
              />
              <Tab
                label={<div className="flex align-items-center">Suspended</div>}
                value="suspended"
              />
            </Tabs>
          </div>

          <div className="w-full flex-grow" style={{ overflowY: "auto" }}>
            <table
              className="primary header-light-navy w-full"
              style={{ borderCollapse: "collapse", tableLayout: "fixed" }}
            >
              <thead className="h--40">
                <tr className="sm">
                  <th className="t--transform--none">Entity</th>
                  <th className="t--transform--none">Manufacturer</th>
                  <th className="t--transform--none t--left" colSpan="2">
                    Pharmacy
                  </th>
                </tr>
              </thead>

              <tbody>
                {activePharmacies.sort(pharmacySort).map((pharmacy, index) => (
                  <tr className="sm t--sm border--bottom" key={index}>
                    <td>{pharmacy.id_340b}</td>
                    <td>
                      <div className="mb-1 t--500">{pharmacy.manufacturer}</div>
                      <div className="t--secondary">{capitalize(pharmacy.network)}</div>
                    </td>
                    <td className="t--left" colSpan="2">
                      <div className="t--500 t--truncate mb-1">{pharmacy.name}</div>
                      <div className="t--secondary t--truncate">{pharmacy.address}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </FetchBoundary>
      </div>
    </div>
  );
};

export default withTheme(AtRiskSuspendedPharmacyCard);
